import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";

import "../../../../../sass/components/organisms/drawer/Drawer.scss";

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode; // Use of drawer can either be "hardcoded mode" where children are specified in the markup
  content?: React.ReactNode; // or can use the `content` property to dynamically swap in/out drawer content.
};

export type DrawerRef = {
  open: () => void;
  close: () => void;
};

const Drawer = forwardRef<DrawerRef, DrawerProps>(({ isOpen, onClose, children, content }, ref) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useImperativeHandle(ref, () => ({
    open: () => {
      if (dialogRef.current && !dialogRef.current.open) {
        dialogRef.current.showModal();
      }
    },
    close: () => {
      if (dialogRef.current && dialogRef.current.open) {
        dialogRef.current.close();
      }
    },
  }));

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const dialogElement = dialogRef.current;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    if (dialogElement) {
      dialogElement.addEventListener("cancel", handleCancel);
      return () => {
        dialogElement.removeEventListener("cancel", handleCancel);
      };
    }
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className="drawer" onClick={onClose}>
      <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
        <>
          {content ? content : children}
          <button onClick={onClose} className="drawer-close" title="Close">
            X
          </button>
        </>
      </div>
    </dialog>
  );
});

export default Drawer;
